import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import RecipeOverview from './pages/RecipeOverview';
import RecipeDetail from './pages/RecipeDetail';
import AddRecipe from './pages/AddRecipe';
import Calendar from './pages/Calendar';
import GroceryList from './pages/GroceryList';
import Nutrition from './pages/Nutrition';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import GenerateMealPlan from './pages/GenerateMealPlan';
import ConstraintProfiles from './pages/ConstraintProfiles';
import RecipeScheduler from './pages/RecipeScheduler';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './context/AuthContext';

function App() {
  const location = useLocation();
  const { isAuthenticated, loading } = useAuth();
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';

  // Global safety: Ensure scroll is always enabled on all pages
  // This prevents any page from accidentally leaving scroll disabled
  useEffect(() => {
    // On initial mount, ensure scroll is enabled
    if (document.body) {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
  }, []);

  // Ensure scroll is enabled when navigating to non-Calendar pages
  useEffect(() => {
    // Only disable scroll if we're on Calendar page AND a drag is active
    // For all other pages, ensure scroll is enabled
    if (location.pathname !== '/calendar') {
      // Not on Calendar page - ensure scroll is enabled
      if (document.body) {
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
      }
    }
  }, [location.pathname]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  // Redirect authenticated users away from auth pages
  if (isAuthPage && isAuthenticated) {
    return <Navigate to="/recipes" replace />;
  }

  // Redirect root path to login if not authenticated, or to recipes if authenticated
  if (location.pathname === '/') {
    return <Navigate to={isAuthenticated ? "/recipes" : "/login"} replace />;
  }

  return (
    <div className="min-h-screen w-full">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/recipes" element={<ProtectedRoute><RecipeOverview /></ProtectedRoute>} />
        <Route path="/recipes/new" element={<ProtectedRoute><AddRecipe /></ProtectedRoute>} />
        <Route path="/recipes/:id" element={<ProtectedRoute><RecipeDetail /></ProtectedRoute>} />
        <Route path="/recipes/:id/edit" element={<ProtectedRoute><AddRecipe /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
        <Route path="/grocery-list" element={<ProtectedRoute><GroceryList /></ProtectedRoute>} />
        <Route path="/nutrition" element={<ProtectedRoute><Nutrition /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
        <Route path="/generate-meal-plan" element={<ProtectedRoute><GenerateMealPlan /></ProtectedRoute>} />
        <Route path="/constraint-profiles" element={<ProtectedRoute><ConstraintProfiles /></ProtectedRoute>} />
        <Route path="/cooking-timeline" element={<ProtectedRoute><RecipeScheduler /></ProtectedRoute>} />
      </Routes>
    </div>
  );
}

export default App;